/**
 * On-demand loader for third-party scripts.
 *
 * Vendor widgets are routinely dropped into a Blade template with a plain
 * `<script src>`, which makes every visitor pay for them on first paint even
 * when the feature is never used. This moves that cost to the moment the feature
 * is actually needed.
 *
 * The loader is idempotent and returns the same promise for repeat calls, so
 * callers can `await` freely without guarding against a double injection.
 */

const pendingScripts = new Map();

/**
 * Inject a `<script>` once, resolving when it has executed.
 *
 * @param {string} src
 * @returns {Promise<void>}
 */
export const loadScript = (src) => {
    if (pendingScripts.has(src)) {
        return pendingScripts.get(src);
    }

    const pending = new Promise((resolve, reject) => {
        const script = document.createElement('script');

        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            /*
             * Drop the rejected promise so a later attempt can retry rather than
             * inheriting this failure forever -- vendor CDNs blip.
             */
            pendingScripts.delete(src);
            reject(new Error(`Failed to load script: ${src}`));
        };

        document.head.appendChild(script);
    });

    pendingScripts.set(src, pending);

    return pending;
};
